/**
 * Utility layout classes generator.
 * For use in Lit components, separate exports are provided that wrap the code in `unsafeCSS` so that they can be used directly in the `styles` array.
 * Also provided are exports that bundle all classes in this file: `layout` and `litLayout`.
 */
import { unsafeCSS } from 'lit';
// Defs
const screenSizes = ['xxxs', 'xxs', 'xs', 's', 'm', 'l', 'max'];
const spacingSizes = ['xxxs', 'xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl', 'xxxxl'];
// Factory for generating classes for a given list of size tokens
const factory = (sizes, content) => {
    return sizes.map((size) => content(size)).join('\n');
};
// Pillar helper
const pillarBase = (size) => {
    return `
        box-sizing: content-box;
        width: calc(100% - 2 * var(--gutter-s));
        max-width: var(--screen-${size});
        margin-inline-start: auto;
        margin-inline-end: auto;
        padding-inline-start: var(--gutter-s);
        padding-inline-end: var(--gutter-s);
    `;
};
/**
 * Pillar
 * Horizontally centers a block, gives it horizontal padding, and restrains its width to the given width token
 * @example `<div class="pillar-m">`
 */
export const pillar = factory(screenSizes, (size) => {
    return `
        .pillar-${size} {
            ${pillarBase(size)}
        }
    `;
});
export const litPillar = unsafeCSS(pillar);
/**
 * Pillar (non-mobile)
 * As above, but not on mobile
 */
export const desktopPillar = factory(screenSizes, (size) => {
    return `
    @media (min-width: 768px) {
        .desktop-pillar-${size} {
            ${pillarBase(size)}
        }
    }
    `;
});
export const litDesktopPillar = unsafeCSS(desktopPillar);
/**
 * Width restrainer
 * Unlike `pillar`, this does not center or pad.
 */
export const restrain = factory(screenSizes, (size) => {
    return `
        .restrain-${size} {
            width: var(--screen-${size});
            max-width: 100%;
        }
    `;
});
export const litRestrain = unsafeCSS(restrain);
/**
 * Vertical spacer
 * Sets the vertical spacing of a block above and below according to the given spacing token
 * @example `<div class="vspace-m">`
 */
export const vspace = factory(spacingSizes, (size) => {
    return `
        .vspace-${size}:not(:empty) {
            margin-block-start: var(--spacing-${size});
            margin-block-end: var(--spacing-${size});
        }
    `;
});
export const litVspace = unsafeCSS(vspace);
/**
 * Padding
 * Sets the padding of a box
 * @example `<div class="pad-m">`
 */
export const pad = factory(spacingSizes, (size) => {
    return `
        .pad-${size} {
            padding: var(--spacing-${size});
        }
    `;
});
export const litPad = unsafeCSS(pad);
/* ======================
 * Flex and grid  helpers
 * ====================== */
/**
 * Gap
 */
export const gap = factory(spacingSizes, (size) => {
    return `
        .gap-${size} {
            gap: var(--spacing-${size});
        }
    `;
});
export const litGap = unsafeCSS(gap);
/**
 * Flex containers
 */
export const flex = `
    .flex {
        display: flex;
    }

    .flex-col {
        flex-direction: column;
    }

    .flex-wrap {
        flex-wrap: wrap;
    }

    .flex-align-start {
        align-items: start;
    }

    .flex-align-center {
        align-items: center;
    }

    .flex-align-end {
        align-items: end;
    }

    .flex-align-baseline {
        align-items: baseline;
    }

    .flex-align-self-start {
        align-self: start;
    }

    .flex-align-self-center {
        align-self: center;
    }

    .flex-justify-start {
        justify-content: start;
    }

    .flex-justify-center {
        justify-content: center;
    }

    .flex-justify-end {
        justify-content: end;
    }

    .flex-justify-self-center {
        justify-self: center;
    }

    .flex-justify-self-end {
        justify-self: flex-end;
    }

    .flex-around {
        justify-content: space-around;
    }

    .flex-between {
        justify-content: space-between;
    }

    .flex-grow-children > * {
        display: flex;
        flex-grow: 1;
    }

    .flex-equal-children > * {
        flex: 1 1 0;
    }

    .flex-grow {
        flex-grow: 1;
    }
`;
export const litFlex = unsafeCSS(flex);
/**
 * Grid containers
 */
export const grid = `
    .grid {
        display: grid;
    }

    .grid-cols-span-all {
        grid-column: 1 / -1;
    }

    .grid-cols-subgrid  {
        grid-template-columns: subgrid;
    }
`;
export const litGrid = unsafeCSS(grid);
/**
 * Width
 */
export const width = `
    .width-100pct {
        width: 100%;
    }
`;
export const litWidth = unsafeCSS(width);
/**
 * Text align
 */
export const textAlign = `
    .text-align-center {
        text-align: center;
    }
`;
export const litTextAlign = unsafeCSS(textAlign);
/**
 * Bundled exports
 */
export const layout = [pillar, desktopPillar, restrain, vspace, pad, gap, flex, width, textAlign].join('\n');
export const litLayout = unsafeCSS(layout);
